import * as Api from '@ViewModels';
import { ResourceAutoCompleteViewModelType } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import { useUpdateDelegatedAccessMutation } from '../../../queries/User/useUpdateDelegatedAccessMutation';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { EditItemPenIcon } from '../../../web/components/svgs/icons/EditItemPenIcon';
import { RoundAddIcon } from '../../../web/components/svgs/icons/RoundAddIcon';
import { TrashIcon } from '../../../web/components/svgs/icons/TrashIcon';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../web/components-v2/Combobox';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Field, FieldContent, FieldDescription, FieldLabel } from '../../../web/components-v2/Field';
import { Input } from '../../../web/components-v2/Input';
import { styleSheet } from './styles';

const SUBADMIN_INPUT_ID = 'subadmins-panel-subadmin-input';
const MEETINGS_CHECKBOX_ID = 'subadmins-panel-meetings-checkbox';

const isSubadmin = (user: Api.IUser) =>
	(user.delegatedAccess?.userIds?.length ?? 0) > 0 || !!user.delegatedAccess?.meetings?.admin;

/** Matches the API's notion of a non-deactivated user: invited-but-not-yet-activated counts, canceled does not. */
const canBecomeSubadmin = (user: Api.IUser) =>
	user.activationStatus === Api.ActivationStatus.ACTIVE || user.activationStatus === Api.ActivationStatus.INIT;

interface ISubadminDraft {
	/** The user being granted delegated access over others */
	userId: string;
	/** Users the subadmin can administer */
	userIds: string[];
	meetingsAdmin: boolean;
	/** True when editing an existing subadmin — the subadmin picker is locked */
	isEditing: boolean;
}

export interface ISubadminsPanelProps {
	impersonationContext: Api.IImpersonationContext;
}

/**
 * Manage delegatedAccess per user on an account. A subadmin is a user with delegated access over one or more
 * other users; rows can be added, edited, or removed.
 */
export const SubadminsPanel: React.FC<ISubadminsPanelProps> = ({ impersonationContext }) => {
	const { logApiError } = useEventLogging('SubadminsPanel');
	const [draft, setDraft] = React.useState<ISubadminDraft | null>(null);
	const [showDelegateSearch, setShowDelegateSearch] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const [subadminSearch, setSubadminSearch] = React.useState('');
	const [pendingDelete, setPendingDelete] = React.useState<Api.IUser | null>(null);

	const { users, isLoading, updateUser } = useLoadAllUsers({ impersonationContext });

	const { mutate: updateDelegatedAccess, isPending: saving } = useUpdateDelegatedAccessMutation({
		impersonationContext,
		onError: error => {
			logApiError('SaveDelegatedAccess', error);
			toast.error(error?.systemMessage || 'Failed to save subadmin. Please try again.');
		},
		onSuccess: (_, { delegatedAccess, user }) => updateUser({ ...user, delegatedAccess }),
	});

	const usersById = React.useMemo(() => new Map(users.map(u => [u.id, u] as const)), [users]);

	const getUserName = (userId: string) => {
		const user = usersById.get(userId);
		return user ? getDisplayName(user) : userId;
	};

	const subadmins = React.useMemo(() => users.filter(isSubadmin), [users]);

	const filteredSubadmins = React.useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) {
			return subadmins;
		}
		return subadmins.filter(u => getDisplayName(u).toLowerCase().includes(query));
	}, [subadmins, search]);

	/**
	 * Users that can be made a subadmin. Inactive users are excluded here only — the full list still backs the
	 * existing-subadmin rows and delegate name lookups, since a user can be deactivated while holding a grant.
	 */
	const addableUsers = React.useMemo(() => {
		const subadminIds = new Set(subadmins.map(u => u.id));
		return users.filter(u => !subadminIds.has(u.id) && canBecomeSubadmin(u));
	}, [users, subadmins]);

	const onAddSubadmin = () => {
		setSubadminSearch('');
		setShowDelegateSearch(false);
		setDraft({
			isEditing: false,
			meetingsAdmin: false,
			userId: '',
			userIds: [],
		});
	};

	const selectedSubadmin = React.useMemo(
		() => addableUsers.find(u => u.id === draft?.userId) ?? null,
		[addableUsers, draft?.userId]
	);

	const onSubadminSelected = (user: Api.IUser | null) => {
		setSubadminSearch(user ? getDisplayName(user) : '');
		setDraft(prev => ({
			...prev,
			userId: user?.id ?? '',
			userIds: user ? prev.userIds.filter(id => id !== user.id) : [],
		}));
	};

	const onEditSubadmin = (user: Api.IUser) => {
		setShowDelegateSearch(false);
		setDraft({
			isEditing: true,
			meetingsAdmin: !!user.delegatedAccess?.meetings?.admin,
			userId: user.id,
			userIds: [...(user.delegatedAccess?.userIds ?? [])],
		});
	};

	const onDelegateSearchSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
		const user = e.selection as Api.IUser;
		if (user.id === draft?.userId) {
			toast.error('A subadmin cannot be given access to themselves.');
			return;
		}
		setDraft(prev => ({
			...prev,
			userIds: [...prev.userIds.filter(id => id !== user.id), user.id],
		}));
		setShowDelegateSearch(false);
	};

	const onRemoveDelegate = (userId: string) => {
		setDraft(prev => ({
			...prev,
			userIds: prev.userIds.filter(id => id !== userId),
		}));
	};

	const onSaveDraft = () => {
		if (!draft?.userId) {
			return;
		}

		const targetUser = usersById.get(draft.userId);
		if (!targetUser) {
			return;
		}

		const delegatedAccess: Api.IDelegatedAccess = {
			userIds: draft.userIds,
			...(draft.meetingsAdmin ? { meetings: { admin: true } } : {}),
		};

		updateDelegatedAccess(
			{ delegatedAccess, user: targetUser },
			{
				onSuccess: () => {
					setDraft(null);
					toast.success(`Permissions saved for ${getDisplayName(targetUser)}.`);
				},
			}
		);
	};

	const onConfirmDelete = () => {
		const user = pendingDelete;
		if (!user) {
			return;
		}

		updateDelegatedAccess(
			{ delegatedAccess: {}, user },
			{
				onSuccess: () => {
					setPendingDelete(null);
					toast.success(`Permissions removed for ${getDisplayName(user)}.`);
				},
			}
		);
	};

	const onCancelDraft = () => {
		setShowDelegateSearch(false);
		setDraft(null);
	};

	const canSave = !!draft?.userId && draft.userIds.length > 0;

	const draftUserName = draft?.userId ? getUserName(draft.userId) : '';

	const getDelegatesLabel = (user: Api.IUser) => {
		const delegateIds = user.delegatedAccess?.userIds ?? [];
		return delegateIds.length ? delegateIds.map(getUserName).join(', ') : 'No users';
	};

	const renderForm = () => (
		<>
			<DialogHeader>
				<DialogTitle>{draft.isEditing ? 'Edit Subadmin' : 'Add Subadmin'}</DialogTitle>
				{draft.isEditing && <DialogDescription>{draftUserName}</DialogDescription>}
			</DialogHeader>

			<DialogBody styles={[styleSheet.formBody]}>
				{!draft.isEditing && (
					<Field>
						<FieldLabel htmlFor={SUBADMIN_INPUT_ID}>Subadmin</FieldLabel>
						<Combobox
							inputValue={subadminSearch}
							isItemEqualToValue={(item: Api.IUser, value: Api.IUser) => item.id === value.id}
							items={addableUsers}
							itemToStringLabel={getDisplayName}
							itemToStringValue={(item: Api.IUser) => item.id}
							onInputValueChange={setSubadminSearch}
							onValueChange={onSubadminSelected}
							value={selectedSubadmin}
						>
							<ComboboxInput id={SUBADMIN_INPUT_ID} placeholder='Search users...' showClear={!!draft.userId} />
							<ComboboxContent>
								<ComboboxEmpty>No matching users.</ComboboxEmpty>
								<ComboboxList>
									{(user: Api.IUser) => (
										<ComboboxItem key={user.id} value={user}>
											{getDisplayName(user)}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</Field>
				)}

				<div className={css(styleSheet.formSection)}>
					<div className={css(styleSheet.formSectionHeading)}>Users</div>

					{draft.userId ? (
						<div className={css(styleSheet.delegateList)}>
							{draft.userIds.map(id => (
								<div key={id} className={css(styleSheet.delegateChip)}>
									<span>{getUserName(id)}</span>
									<Button
										aria-label={`Remove ${getUserName(id)}`}
										onClick={() => onRemoveDelegate(id)}
										size='icon'
										styles={[styleSheet.iconButton]}
										title='Remove'
										variant='ghost'
									>
										<XIcon height={12} width={12} />
									</Button>
								</div>
							))}

							{!draft.userIds.length && (
								<div className={css(styleSheet.formSectionEmpty)}>Add at least one user to save this subadmin.</div>
							)}

							<Button onClick={() => setShowDelegateSearch(true)} size='sm' variant='link'>
								{'+ Add user'}
							</Button>
						</div>
					) : (
						<div className={css(styleSheet.formSectionEmpty)}>Select a subadmin above to add users.</div>
					)}
				</div>

				<div className={css(styleSheet.formSection)}>
					<div className={css(styleSheet.formSectionHeading)}>Permissions</div>

					<Field orientation='horizontal'>
						<Checkbox
							checked={draft.meetingsAdmin}
							id={MEETINGS_CHECKBOX_ID}
							onCheckedChange={checked => setDraft(prev => ({ ...prev, meetingsAdmin: checked === true }))}
						/>
						<FieldContent>
							<FieldLabel htmlFor={MEETINGS_CHECKBOX_ID}>Meetings</FieldLabel>
							<FieldDescription>Allows managing meetings for the users above.</FieldDescription>
						</FieldContent>
					</Field>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button disabled={saving} onClick={onCancelDraft} variant='outline'>
					Cancel
				</Button>
				<Button disabled={!canSave} isLoading={saving} onClick={onSaveDraft}>
					Save
				</Button>
			</DialogFooter>

			<Dialog onOpenChange={setShowDelegateSearch} open={showDelegateSearch}>
				<DialogPopup styles={[styleSheet.searchPopup]}>
					<DialogHeader>
						<DialogTitle>Add User</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<SimpleAutoCompleteSearchField
							impersonationContext={impersonationContext}
							onItemSelected={onDelegateSearchSelected}
							pageSize={8}
							placeholder='Search users...'
							resultsLimit={8}
							type={ResourceAutoCompleteViewModelType.User}
						/>
					</DialogBody>
				</DialogPopup>
			</Dialog>
		</>
	);

	return (
		<>
			<div className={css(styleSheet.pagePanel)}>
				{isLoading ? (
					<LoadingSpinner />
				) : (
					<>
						<div className={css(styleSheet.title)}>Manage Subadmins</div>

						{subadmins.length > 0 && (
							<Input
								aria-label='Search subadmins by name'
								onChange={e => setSearch(e.target.value)}
								placeholder='Search by name...'
								type='text'
								value={search}
							/>
						)}

						{subadmins.length === 0 && (
							<div className={css(styleSheet.emptyState)}>No subadmins configured. Click below to add one.</div>
						)}

						{subadmins.length > 0 && filteredSubadmins.length === 0 && (
							<div className={css(styleSheet.emptyState)}>No subadmins match your search.</div>
						)}

						{filteredSubadmins.map(user => (
							<div key={user.id} className={css(styleSheet.subadminRow)}>
								<div className={css(styleSheet.subadminRowDetails)}>
									<div className={css(styleSheet.subadminRowTitle)}>{getDisplayName(user)}</div>
									<div className={css(styleSheet.subadminRowSubtitle)}>
										{'Manages: '}
										{getDelegatesLabel(user)}
									</div>
								</div>
								<Button
									disabled={saving}
									onClick={() => onEditSubadmin(user)}
									size='icon'
									styles={[styleSheet.iconButton]}
									title='Edit subadmin'
									variant='ghost'
								>
									<EditItemPenIcon />
								</Button>
								<Button
									disabled={saving}
									onClick={() => setPendingDelete(user)}
									size='icon'
									styles={[styleSheet.iconButton]}
									title='Remove subadmin'
									variant='destructive-secondary'
								>
									<TrashIcon />
								</Button>
							</div>
						))}

						<Button disabled={saving} onClick={onAddSubadmin} styles={[styleSheet.addButton]} variant='link'>
							<RoundAddIcon />
							<span>Add Subadmin</span>
						</Button>
					</>
				)}
			</div>

			<Dialog
				onOpenChange={open => {
					if (!open) {
						onCancelDraft();
					}
				}}
				open={!!draft}
			>
				<DialogPopup>{!!draft && renderForm()}</DialogPopup>
			</Dialog>

			<Dialog
				onOpenChange={open => {
					if (!open) {
						setPendingDelete(null);
					}
				}}
				open={!!pendingDelete}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Remove subadmin?</DialogTitle>
						<DialogDescription>
							{pendingDelete
								? `Are you sure you want to remove subadmin permissions for ${getDisplayName(pendingDelete)}?`
								: ''}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button disabled={saving} onClick={() => setPendingDelete(null)} variant='outline'>
							Cancel
						</Button>
						<Button isLoading={saving} onClick={onConfirmDelete} variant='destructive'>
							Remove
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
};
